/**
 * TwoColumnATSTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 97 / 100 — Verified
 * Style: Two-column layout — left sidebar for skills/education, right for experience
 * Best For: Mid-level professionals with strong skills and experience balance
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList, EmptyState,
  safePoint,
} from './templateDesignSystem';
import { categorizeSkills } from './templateHelpers';

export const ATS_META = {
  atsRating: 97,
  atsLabel: 'ATS Verified',
  bestFor: 'Mid-level professionals, balanced layout',
  compatibility: 'Works with Workday, Greenhouse, Lever',
  recommendedFor: 'Engineers, analysts, managers with 2-8 yrs experience',
};

function TwoColumnATSTemplate({
  resumeData,
  accentColor = '#1e3a5f',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => {},
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const {
    name, role, summaryText, skills, experience, internships,
    projects, educationList, certList, achieveList, extraList, languageList,
  } = d;

  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const SideSection = ({ title, children }) => (
    <div style={{ marginBottom: 14 }}>
      <h2 style={{
        fontSize: fs('9px'), fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '1.5px', color: accentColor, margin: '0 0 6px',
        paddingBottom: 3, borderBottom: `1.5px solid ${accentColor}40`,
        fontFamily: 'inherit',
      }}>{title}</h2>
      {children}
    </div>
  );

  const MainSection = ({ title, children }) => (
    <div style={{ marginBottom: 12 }}>
      <h2 style={{
        fontSize: fs('10px'), fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '1.2px', color: accentColor, margin: '0 0 6px',
        paddingBottom: 4, borderBottom: `2px solid ${accentColor}`,
        fontFamily: 'inherit',
      }}>{title}</h2>
      {children}
    </div>
  );

  // Skills for sidebar
  const cats = categorizeSkills(skills);

  return (
    <div
      id="resume"
      style={{
        background: '#fff',
        color: '#111',
        fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
        fontSize: fs('10px'),
        lineHeight: 1.5,
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      {/* ─── Header ─── */}
      <header style={{
        padding: '20px 28px 14px',
        borderBottom: `3px solid ${accentColor}`,
        background: `${accentColor}08`,
      }}>
        <EditableField
          editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)}
          tag="h1"
          style={{ fontSize: fs('22px'), fontWeight: 800, color: '#111', margin: 0, letterSpacing: '-0.5px' }}
          placeholder="Your Name"
        />
        {(role || editMode) && (
          <EditableField
            editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)}
            tag="p"
            style={{ fontSize: fs('11px'), fontWeight: 600, color: accentColor, margin: '3px 0 0', textTransform: 'uppercase', letterSpacing: '0.8px' }}
            placeholder="Your Professional Title"
          />
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', marginTop: 6 }}>
          {d.contactItems.map((item, i) => (
            <span key={i} style={{ fontSize: fs('9px'), color: '#6B7280' }}>{item}</span>
          ))}
          {d.linkItems.map((l, i) => (
            <span key={`l${i}`} style={{ fontSize: fs('9px'), color: '#6B7280' }}>{l.label}</span>
          ))}
        </div>
      </header>

      {/* ─── Two Column Body ─── */}
      <div style={{ display: 'flex', padding: '0' }}>
        {/* ── Left Sidebar ── */}
        <aside style={{
          width: '32%', flexShrink: 0,
          padding: '16px 14px 16px 28px',
          borderRight: `1px solid ${accentColor}18`,
          background: `${accentColor}04`,
        }}>
          {/* Skills */}
          {skills.length > 0 && (
            <SideSection title="Skills">
              {cats.length > 1 ? (
                cats.map((cat, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <p style={{ fontSize: fs('8.5px'), fontWeight: 700, color: '#111', margin: '0 0 2px' }}>{cat.label}</p>
                    <p style={{ fontSize: fs('9px'), color: '#4B5563', margin: 0, lineHeight: 1.5 }}>{cat.items.join(', ')}</p>
                  </div>
                ))
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px 5px' }}>
                  {skills.map((s, i) => (
                    <span key={i} style={{
                      fontSize: fs('8.5px'), fontWeight: 500, color: '#374151',
                      background: `${accentColor}10`, border: `1px solid ${accentColor}20`,
                      borderRadius: 3, padding: '2px 6px',
                    }}>{s}</span>
                  ))}
                </div>
              )}
            </SideSection>
          )}

          {/* Education */}
          {educationList.length > 0 && (
            <SideSection title="Education">
              {educationList.map((edu, idx) => (
                <div key={idx} style={{ marginBottom: 8 }}>
                  <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                    tag="p" style={{ fontSize: fs('9.5px'), fontWeight: 700, color: '#111', margin: 0, lineHeight: 1.3 }} />
                  <p style={{ fontSize: fs('8.5px'), color: '#6B7280', margin: '1px 0 0' }}>{edu?.college}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 1 }}>
                    <EditableField editMode={editMode} value={edu?.year || ''} onSave={v => onFieldUpdate(`education.${idx}.year`, v)}
                      tag="span" style={{ fontSize: fs('8px'), color: '#9CA3AF', fontStyle: 'italic' }} />
                    {edu?.cgpa && <span style={{ fontSize: fs('8px'), color: '#9CA3AF' }}>{edu.cgpa}</span>}
                  </div>
                </div>
              ))}
            </SideSection>
          )}

          {/* Certifications */}
          {certList.length > 0 && (
            <SideSection title="Certifications">
              {certList.map((cert, i) => {
                const text = safePoint(cert);
                return text ? (
                  <p key={i} style={{ fontSize: fs('9px'), color: '#374151', margin: '0 0 3px', lineHeight: 1.4 }}>• {text}</p>
                ) : null;
              })}
            </SideSection>
          )}

          {/* Languages */}
          {languageList.length > 0 && (
            <SideSection title="Languages">
              {languageList.map((lang, i) => (
                <p key={i} style={{ fontSize: fs('9px'), color: '#374151', margin: '0 0 2px' }}>{lang}</p>
              ))}
            </SideSection>
          )}
        </aside>

        {/* ── Right Main Content ── */}
        <main style={{ flex: 1, padding: '16px 28px 16px 18px' }}>
          {/* Summary */}
          {(summaryText || editMode) && (
            <MainSection title="Professional Summary">
              <EditableField
                editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
                tag="p" multiline
                style={{ fontSize: fs('10px'), color: '#374151', lineHeight: 1.65, margin: 0 }}
                placeholder="Write a professional summary..."
              />
            </MainSection>
          )}

          {/* Experience */}
          {experience.length > 0 && (
            <MainSection title="Work Experience">
              {experience.map((exp, idx) => (
                <div key={idx} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div>
                      <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                        tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                      {exp.company && <span style={{ color: '#6B7280', fontSize: fs('10px') }}>{' — '}{exp.company}</span>}
                    </div>
                    <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                      tag="span" style={{ fontSize: fs('9px'), color: '#9CA3AF', whiteSpace: 'nowrap', fontStyle: 'italic' }} />
                  </div>
                  <BulletList points={exp.points} accentColor={accentColor} />
                </div>
              ))}
            </MainSection>
          )}

          {/* Internships */}
          {internships.length > 0 && (
            <MainSection title="Internships">
              {internships.map((exp, idx) => (
                <div key={idx} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div>
                      <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                        tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111', margin: 0, display: 'inline' }} />
                      {exp.company && <span style={{ color: '#6B7280', fontSize: fs('10px') }}>{' — '}{exp.company}</span>}
                    </div>
                    <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                      tag="span" style={{ fontSize: fs('9px'), color: '#9CA3AF', whiteSpace: 'nowrap', fontStyle: 'italic' }} />
                  </div>
                  <BulletList points={exp.points} accentColor={accentColor} />
                </div>
              ))}
            </MainSection>
          )}

          {/* Projects */}
          {projects.length > 0 && (
            <MainSection title="Projects">
              {projects.map((proj, idx) => (
                <div key={idx} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#111' }}>{proj.title || proj.name}</span>
                      {proj.tech?.length > 0 && (
                        <span style={{ fontSize: fs('9px'), color: accentColor, marginLeft: 6, fontStyle: 'italic' }}>
                          [{Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}]
                        </span>
                      )}
                    </div>
                  </div>
                  <BulletList points={proj.points} accentColor={accentColor} />
                </div>
              ))}
            </MainSection>
          )}

          {/* Achievements */}
          {achieveList.length > 0 && (
            <MainSection title="Achievements">
              <BulletList points={achieveList} accentColor={accentColor} />
            </MainSection>
          )}

          {/* Extracurricular */}
          {extraList.length > 0 && (
            <MainSection title="Extracurricular Activities">
              <BulletList points={extraList} accentColor={accentColor} />
            </MainSection>
          )}
        </main>
      </div>
    </div>
  );
}

export default TwoColumnATSTemplate;
